import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { GraduationCap, Mail, Lock, AlertCircle, User, Shield, BookOpen, UserCircle } from 'lucide-react';

// Demo accounts — Teacher & Student are for SMD Higher Secondary School (school id 2)
const DEMO_CREDENTIALS = [
  { label: 'Super Admin', email: 'superadmin@schoolerp.com', password: 'Admin@123', icon: Shield, color: 'text-purple-400' },
  { label: 'School Admin', email: 'schooladmin@demoschool.edu', password: 'Admin@123', icon: User, color: 'text-blue-400' },
  { label: 'Demo Teacher', email: 'teacher@demoschool.edu', password: 'Teacher@123', icon: BookOpen, color: 'text-green-400' },
  { label: 'Demo Student', email: 'student@demoschool.edu', password: 'Student@123', icon: UserCircle, color: 'text-amber-400' },
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const sso = searchParams.get('sso');
    if (sso === 'missing') {
      setError('Single sign-on link was invalid. Sign in below.');
    } else if (sso === 'error') {
      setError('Single sign-on failed or the link expired. Sign in below.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  };

  const fillAndLogin = async (cred) => {
    setEmail(cred.email);
    setPassword(cred.password);
    setError('');
    setLoading(true);
    const result = await login(cred.email, cred.password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="absolute inset-0 bg-slate-900/50 opacity-40"></div>
      
      <Card className="w-full max-w-md relative z-10 bg-slate-800/80 backdrop-blur-xl border-slate-700 shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
            <GraduationCap className="w-9 h-9 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">School ERP</CardTitle>
          <CardDescription className="text-slate-400">Sign in to manage your school</CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-5"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-slate-700/30 rounded-lg border border-slate-600">
            <p className="text-xs font-medium text-slate-300 mb-3">Demo login (SMD Higher Secondary School) — use any below:</p>
            <div className="space-y-2">
              {DEMO_CREDENTIALS.map((cred) => {
                const Icon = cred.icon;
                return (
                  <div key={cred.email} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-600/50 last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className={`w-4 h-4 shrink-0 ${cred.color}`} />
                      <span className="text-slate-400 text-xs">{cred.label}:</span>
                      <span className="text-slate-300 text-xs truncate">{cred.email}</span>
                      <span className="text-slate-500 text-xs">/ {cred.password}</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="shrink-0 h-7 text-xs border-slate-500 text-slate-300 hover:bg-slate-600 hover:text-white"
                      onClick={() => fillAndLogin(cred)}
                      disabled={loading}
                    >
                      Use & sign in
                    </Button>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-2">API: {process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}/api</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
